import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faSearch, faBan, faCheck, faEye, faEdit } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

export default function UserManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  }

  async function handleSuspendUser(userId, suspend) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        suspended: suspend,
        suspendedAt: suspend ? new Date() : null,
        suspendedBy: suspend ? currentUser.uid : null
      });
      toast.success(suspend ? 'User suspended' : 'User activated');
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  }

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.displayName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.familyId?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-text-primary flex items-center gap-3">
                <FontAwesomeIcon icon={faUsers} className="text-4xl text-primary" />
                User Management
              </h1>
              <p className="text-text-secondary mt-1">
                View and manage platform users
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by name, email, or family ID..."
              className="w-full pl-12 pr-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Family</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Joined</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginatedUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                          {user.displayName?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-text-primary">{user.displayName || 'Unknown'}</div>
                          <div className="text-xs text-text-secondary">ID: {user.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">{user.email || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary">{user.familyId || 'None'}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {user.createdAt?.toDate ? format(user.createdAt.toDate(), 'MMM dd, yyyy') : 'Unknown'}
                    </td>
                    <td className="px-6 py-4">
                      {user.suspended ? (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Suspended</span>
                      ) : (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                          title="View details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        {user.suspended ? (
                          <button
                            onClick={() => handleSuspendUser(user.id, false)}
                            className="text-green-600 hover:text-green-700 transition-colors"
                            title="Activate user"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspendUser(user.id, true)}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Suspend user"
                            disabled={user.id === currentUser?.uid}
                          >
                            <FontAwesomeIcon icon={faBan} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <div className="text-sm text-text-secondary">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif font-bold text-text-primary">User Details</h2>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUser(null);
                }}
                className="text-text-secondary hover:text-text-primary"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-text-secondary">Display Name</label>
                <p className="text-text-primary">{selectedUser.displayName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-secondary">Email</label>
                <p className="text-text-primary">{selectedUser.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-secondary">User ID</label>
                <p className="text-text-primary font-mono text-sm">{selectedUser.id}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-secondary">Family ID</label>
                <p className="text-text-primary">{selectedUser.familyId || 'None'}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-secondary">Joined</label>
                <p className="text-text-primary">
                  {selectedUser.createdAt?.toDate ? format(selectedUser.createdAt.toDate(), 'PPpp') : 'Unknown'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-secondary">Last Active</label>
                <p className="text-text-primary">
                  {selectedUser.lastActive?.toDate ? format(selectedUser.lastActive.toDate(), 'PPpp') : 'Never'}
                </p>
              </div>
              {selectedUser.suspended && (
                <div>
                  <label className="text-sm font-semibold text-text-secondary">Suspended</label>
                  <p className="text-red-600">Yes - {selectedUser.suspendedAt?.toDate ? format(selectedUser.suspendedAt.toDate(), 'PPpp') : 'Unknown date'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

